import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Typography, Collapse, ConfigProvider } from '@app/ui';
import { shortCurrency, shortDate } from '@app/utils/formatters';
import { BroadcastRsp, Client } from '@app/types/api';
import { generatePath, useNavigate } from 'react-router-dom';
import RouteNames from '../../../RouteNames';
import ExposureTile from './ExposureTile';
import { ExposureType } from '../../utils/broadcastsUtils';

type BroadcastTilesProps = {
    broadcast: any;
    client: Client;
};

const BroadcastTiles = ({ broadcast, client }: BroadcastTilesProps) => {
    const { t } = useTranslation('measurement');
    const navigate = useNavigate();
    const { contentDate, broadcastName, broadcastRsps } = broadcast;
    const [activeKey, setActiveKey] = useState<string[] | null>(['1']);

    const [selectedIndex, setSelectedIndex] = useState<number | undefined>();
    const [showIndex, setShowIndex] = useState(0);
    const currency = client.currency.code;

    useEffect(() => {
        setShowIndex(0);
        setSelectedIndex(undefined);
    }, [broadcastRsps]);

    const localizedBroadcastName = useMemo(
        () =>
            broadcastName.replace(
                /\([0-9]{2}-[0-9]{2}-[0-9]{4}\)/g,
                `(${shortDate(contentDate)})`,
            ),
        [broadcastName, contentDate],
    );

    const selectedBroadcast = useMemo(() => {
        if (!Number.isInteger(selectedIndex)) return undefined;
        return {
            broadcastName,
            contentDate,
            ...broadcastRsps[selectedIndex],
        };
    }, [selectedIndex]);

    useEffect(() => {
        if (selectedBroadcast) {
            const {
                broadcastName,
                contentDate,
                rightsholder,
                sponsor,
                placement,
            } = selectedBroadcast;

            const path = generatePath(RouteNames.broadcastModal.path, {
                broadcastName: encodeURIComponent(broadcastName),
                contentDate: encodeURIComponent(contentDate),
                rightsholderId: encodeURIComponent(rightsholder.id),
                sponsorId: encodeURIComponent(sponsor.id),
                placementId: encodeURIComponent(placement.id),
            });

            navigate(path);
        }
    }, [selectedBroadcast]);

    const handleKeyDown = (e: KeyboardEvent) => {
        if (selectedIndex == null) return;
        if (e.key === 'ArrowLeft' && selectedIndex > 0) {
            setSelectedIndex(selectedIndex - 1);
        } else if (
            e.key === 'ArrowRight' &&
            selectedIndex < broadcastRsps.length - 1
        ) {
            setSelectedIndex(selectedIndex + 1);
        } else if (e.key === 'Escape') {
            setSelectedIndex(undefined);
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [selectedIndex]);

    return (
        <ConfigProvider
            theme={{
                components: {
                    Collapse: {
                        contentPadding: 12,
                    },
                    Card: {
                        bodyPadding: 0,
                    },
                },
            }}
        >
            <Collapse
                activeKey={activeKey}
                onChange={keys => setActiveKey(keys as string[])}
                expandIcon={({ isActive }) => (
                    <i
                        className={`fa-solid fa-chevron-${
                            isActive ? 'up' : 'down'
                        }`}
                        style={{
                            fontSize: '16px',
                            color: '#515252',
                            fontWeight: 'bold',
                        }}
                    />
                )}
                items={[
                    {
                        key: '1',
                        label: (
                            <Flex>
                                <Typography.Text
                                    style={{
                                        fontWeight: 'bold',
                                        marginRight: 8,
                                    }}
                                >
                                    {t('broadcastExposure.eventExposures')}{' '}
                                </Typography.Text>

                                <Typography.Text style={{ margin: 0 }}>
                                    {' '}
                                    {localizedBroadcastName}
                                </Typography.Text>
                            </Flex>
                        ),
                        children: (
                            <Flex
                                gap={16}
                                align="center"
                                style={{
                                    overflowY: 'scroll',
                                }}
                            >
                                {broadcastRsps.map((brsp, idx) => {
                                    const {
                                        rightsholder,
                                        sponsor,
                                        frameUrl,
                                        placement,
                                        sponsorMediaValue,
                                        totalVerbalMentions,
                                    } = brsp as BroadcastRsp;

                                    return (
                                        <ExposureTile
                                            key={`exposure_card_${idx}`}
                                            type={
                                                (totalVerbalMentions ?? 0) > 0
                                                    ? ExposureType.VerbalMention
                                                    : ExposureType.Visual
                                            }
                                            frameUrl={frameUrl}
                                            smv={
                                                sponsorMediaValue != null
                                                    ? shortCurrency(
                                                          sponsorMediaValue,
                                                          2,
                                                          currency,
                                                      )
                                                    : '-'
                                            }
                                            rightsholderLogoUrl={
                                                rightsholder.logoUrl
                                            }
                                            placementName={placement.name}
                                            sponsorName={sponsor.name}
                                            contentDate={contentDate}
                                            onClick={() =>
                                                setSelectedIndex(
                                                    showIndex + idx,
                                                )
                                            }
                                        />
                                    );
                                })}
                            </Flex>
                        ),
                    },
                ]}
            />
        </ConfigProvider>
    );
};

export default BroadcastTiles;
